import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

import { FieldError } from "@/components/ui/field-error";
import { useStockMovements, CreateStockMovementInput } from "@/hooks/use-stock-movements";
import { CostImpactWarning } from "@/components/cost-impact-warning";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { StockInfoCard, StockInfoInline } from "@/components/stock-info-card";
import { isFeatureEnabled } from "@/config/feature-flags";
import { getStockLevel } from "@/data/mock-stock-levels";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { 
  Plus, 
  Trash2, 
  Package, 
  ChevronDown, 
  Info,
  ShoppingCart,
  ShoppingBag,
  Database,
  Trash2 as WriteOffIcon,
  ArrowRightLeft,
  Edit,
  CalendarIcon
} from "lucide-react";
import { cn } from "@/lib/utils";
import { mockLocations, mockWarehouses } from "@/data/mockWarehousesLocations";

// Mock stock items data (temporary until backend integration)
const mockStockItems = [
  { code: "WGT-A", description: "Widget Type A", unitOfMeasure: "EA", unitPrice: 25.00, lastCostPrice: 15.00 },
  { code: "WGT-B", description: "Widget Type B", unitOfMeasure: "EA", unitPrice: 30.00, lastCostPrice: 18.00 },
  { code: "WGT-C", description: "Widget Type C", unitOfMeasure: "EA", unitPrice: 35.00, lastCostPrice: 20.00 },
  { code: "GDG-X", description: "Gadget Model X", unitOfMeasure: "EA", unitPrice: 150.00, lastCostPrice: 90.00 },
  { code: "GDG-Y", description: "Gadget Model Y", unitOfMeasure: "EA", unitPrice: 175.00, lastCostPrice: 105.00 },
  { code: "GDG-Z", description: "Gadget Model Z", unitOfMeasure: "EA", unitPrice: 200.00, lastCostPrice: 120.00 },
  { code: "CMP-001", description: "Component 001", unitOfMeasure: "EA", unitPrice: 5.00, lastCostPrice: 3.00 },
  { code: "CMP-002", description: "Component 002", unitOfMeasure: "EA", unitPrice: 7.50, lastCostPrice: 4.50 },
  { code: "CMP-003", description: "Component 003", unitOfMeasure: "EA", unitPrice: 10.00, lastCostPrice: 6.00 },
  { code: "ASM-100", description: "Assembly 100", unitOfMeasure: "EA", unitPrice: 250.00, lastCostPrice: 150.00 },
];

const writeOffReasons = [
  { value: "damaged", label: "Damaged" },
  { value: "expired", label: "Expired" },
  { value: "obsolete", label: "Obsolete" },
  { value: "theft_loss", label: "Theft/Loss" },
  { value: "quality_issue", label: "Quality Issue" },
  { value: "other", label: "Other" },
];

const adjustmentReasons = [
  { value: "stock_count", label: "Stock Count Correction" },
  { value: "system_error", label: "System Error Correction" },
  { value: "damaged_goods", label: "Damaged Goods" },
  { value: "found_items", label: "Found Items" },
  { value: "data_migration", label: "Data Migration" },
  { value: "other", label: "Other" },
];

const mockProjectCodes = [
  { code: "PRJ-001", name: "Website Redesign 2024", department: "Marketing" },
  { code: "PRJ-002", name: "Factory Expansion Phase 2", department: "Operations" },
  { code: "PRJ-003", name: "Client ABC - Custom Solution", department: "Sales" },
  { code: "PRJ-004", name: "ERP System Upgrade", department: "IT" },
  { code: "PRJ-005", name: "New Product Launch - Widget Pro", department: "Product" },
];

const mockCostCenters = [
  { code: "CC-001", name: "Marketing Department", department: "Marketing" },
  { code: "CC-002", name: "IT Operations", department: "IT" },
  { code: "CC-003", name: "R&D Department", department: "R&D" },
  { code: "CC-004", name: "Facilities Management", department: "Facilities" },
  { code: "CC-005", name: "Human Resources", department: "HR" },
];

type MovementType = "Sales" | "Purchase" | "Opening Balance" | "Write-off" | "Manual Adjustment" | "Transfer Between Locations" | null;
type LineMovementType = "In" | "Out";

interface LineItem {
  id: string;
  stockCode: string;
  stockDescription: string;
  warehouse: string;
  location: string;
  movementDirection: LineMovementType;
  quantity: string;
  unitPrice: string;
  unit: string;
  projectCode: string;
  costCenter: string;
  adjustmentType?: "increase" | "decrease";
  notes: string;
  // Phase 2 fields (optional, controlled by feature flags)
  batchNumber?: string;
  lotNumber?: string;
  serialNumber?: string;
  expiryDate?: Date;
  manufactureDate?: Date;
  errors: {
    stockCode?: string;
    warehouse?: string;
    location?: string;
    movementDirection?: string;
    quantity?: string;
    unitPrice?: string;
    projectCode?: string;
    costCenter?: string;
    adjustmentType?: string;
    batchNumber?: string;
    serialNumber?: string;
  };
}

interface FormData {
  referenceNumber: string;
  movementDate: Date;
  customerReference: string;
  supplierReference: string;
  writeOffReason: string;
  adjustmentReason: string;
  otherReasonDetails: string;
  fromWarehouse: string;
  fromLocation: string;
  toWarehouse: string;
  toLocation: string;
  notes: string;
}

interface FormErrors {
  referenceNumber?: string;
  movementDate?: string;
  customerReference?: string;
  supplierReference?: string;
  writeOffReason?: string;
  adjustmentReason?: string;
  otherReasonDetails?: string;
  fromWarehouse?: string;
  fromLocation?: string;
  toWarehouse?: string;
  toLocation?: string;
  notes?: string;
}

// Last updated: 2026-01-23 14:00:00 - Transfer mode selection removed - FORCE RELOAD
export function UnifiedStockMovementPage() {
  console.log("🔥🔥🔥 UNIFIED STOCK MOVEMENT PAGE LOADED - VERSION 3.1 - TRANSFER FIXED 🔥🔥🔥");
  console.log("🔥 Transfer form is available and ready to render!");
  const { navigateTo, pageParams } = useNavigation();
  const { createMovement } = useStockMovements();
  
  // Use real locations from warehouse data  
  const locations = mockLocations.filter(l => l.active).map(l => l.locationCode);
  
  // Debug: Log locations count
  useEffect(() => {
    console.log(`📍 Total locations available: ${locations.length}`);
    console.log(`📍 Sample locations:`, locations.slice(0, 10));
  }, [locations]);
  
  const [selectedType, setSelectedType] = useState<MovementType>(null);
  const [previousType, setPreviousType] = useState<MovementType>(null);
  const [showTypeChangeDialog, setShowTypeChangeDialog] = useState(false);
  const [pendingType, setPendingType] = useState<MovementType>(null);
  const [prepopulatedItemsCount, setPrepopulatedItemsCount] = useState<number>(0);
  
  const [formData, setFormData] = useState<FormData>({
    referenceNumber: "",
    movementDate: new Date(),
    customerReference: "",
    supplierReference: "",
    writeOffReason: "",
    adjustmentReason: "",
    otherReasonDetails: "",
    fromWarehouse: "",
    fromLocation: "",
    toWarehouse: "",
    toLocation: "",
    notes: "",
  });
  
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [lineItems, setLineItems] = useState<LineItem[]>([
    {
      id: "1",
      stockCode: "",
      stockDescription: "",
      warehouse: "",
      location: "",
      movementDirection: "In",
      quantity: "",
      unitPrice: "",
      unit: "",
      projectCode: "",
      costCenter: "",
      notes: "",
      errors: {},
    },
  ]);
  
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const typeSelectRef = useRef<HTMLButtonElement>(null);

  // Focus on type selector on mount
  useEffect(() => {
    typeSelectRef.current?.focus();
  }, []);

  // Handle pre-populated items from Stock Items page and auto-select type from params
  useEffect(() => {
    // Auto-select movement type if passed via params
    if (pageParams?.type && !selectedType) {
      console.log("🎯 Auto-selecting movement type from params:", pageParams.type);
      setSelectedType(pageParams.type as MovementType);
    }
    
    if (pageParams?.prepopulatedItems && Array.isArray(pageParams.prepopulatedItems)) {
      const items = pageParams.prepopulatedItems;
      setPrepopulatedItemsCount(items.length);
      
      // Auto-select first movement type (Sales) if not already set
      if (!selectedType && !pageParams?.type) {
        setSelectedType("Sales");
      }
      
      // Convert prepopulated items to line items
      const newLineItems: LineItem[] = items.map((item: any, index: number) => ({
        id: String(Date.now() + index),
        stockCode: item.stockCode || "",
        stockDescription: item.stockDescription || "",
        warehouse: item.warehouseCode || "",
        location: item.locationCode || "",
        movementDirection: "In" as LineMovementType,
        quantity: "",
        unitPrice: item.unitCost?.toString() || "",
        unit: item.unitOfMeasure || "",
        projectCode: "",
        costCenter: "",
        notes: "",
        errors: {},
      }));
      
      setLineItems(newLineItems);
      
      // Show success toast
      toast.success(`${items.length} item${items.length > 1 ? "s" : ""} pre-populated from Stock Items`);
    }
  }, [pageParams]);

  // Track unsaved changes
  useEffect(() => {
    const hasFormData =
      formData.referenceNumber ||
      formData.customerReference ||
      formData.supplierReference ||
      formData.writeOffReason ||
      formData.adjustmentReason ||
      formData.otherReasonDetails ||
      formData.fromLocation ||
      formData.toLocation ||
      formData.notes;
    
    const hasLineData = lineItems.some(
      (item) => item.stockCode || item.quantity || item.location || item.notes
    );
    
    setHasUnsavedChanges(selectedType !== null && (hasFormData || hasLineData));
  }, [formData, lineItems, selectedType]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);



  const handleTypeChange = (value: string) => {
    const newType = value as MovementType;
    
    // Check if there's any form data
    if (hasUnsavedChanges) {
      setPendingType(newType);
      setShowTypeChangeDialog(true);
    } else {
      applyTypeChange(newType);
    }
  };

  const applyTypeChange = (newType: MovementType) => {
    console.log("🎯 applyTypeChange called with:", newType);
    console.log("🎯 Is Transfer?", newType === "Transfer Between Locations");
    console.log("🎯 String comparison:", `"${newType}" === "Transfer Between Locations"`, newType === "Transfer Between Locations");
    // Handle all movement types including transfers
    setPreviousType(selectedType);
    setSelectedType(newType);
    console.log("🎯 selectedType updated to:", newType);
    console.log("🎯 After update, isTransferType will be:", newType === "Transfer Between Locations");
    
    // Reset form data
    setFormData({
      referenceNumber: "",
      movementDate: new Date(),
      customerReference: "",
      supplierReference: "",
      writeOffReason: "",
      adjustmentReason: "",
      otherReasonDetails: "",
      fromWarehouse: "",
      fromLocation: "",
      toWarehouse: "",
      toLocation: "",
      notes: "",
    });
    
    // Reset line items
    setLineItems([
      {
        id: "1",
        stockCode: "",
        stockDescription: "",
        warehouse: "",
        location: "",
        movementDirection: "In",
        quantity: "",
        unitPrice: "",
        unit: "",
        projectCode: "",
        costCenter: "",
        notes: "",
        errors: {},
      },
    ]);
    
    setFormErrors({});
  };

  const confirmTypeChange = () => {
    if (pendingType) {
      applyTypeChange(pendingType);
      setPendingType(null);
    }
    setShowTypeChangeDialog(false);
  };

  const cancelTypeChange = () => {
    setPendingType(null);
    setShowTypeChangeDialog(false);
  };

  const getTypeBadgeColor = (type: MovementType) => {
    switch (type) {
      case "Sales":
        return "bg-blue-500 hover:bg-blue-600";
      case "Purchase":
        return "bg-green-500 hover:bg-green-600";
      case "Opening Balance":
        return "bg-purple-500 hover:bg-purple-600";
      case "Write-off":
        return "bg-red-500 hover:bg-red-600";
      case "Manual Adjustment":
        return "bg-gray-500 hover:bg-gray-600";
      case "Transfer Between Locations":
        return "bg-orange-500 hover:bg-orange-600";
      default:
        return "";
    }
  };

  const getReferenceLabel = (type: MovementType) => {
    switch (type) {
      case "Sales":
        return "Sales Reference";
      case "Purchase":
        return "Purchase Reference";
      case "Opening Balance":
        return "Opening Balance Reference";
      case "Write-off":
        return "Write-off Reference";
      case "Manual Adjustment":
        return "Adjustment Reference";
      case "Transfer Between Locations":
        return "Transfer Reference";
      default:
        return "Reference Number";
    }
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleStockCodeChange = (id: string, code: string) => {
    const stockItem = mockStockItems.find((item) => item.code === code);
    
    // Auto-populate price based on movement type
    let autoPrice = "";
    if (selectedType === "Sales") {
      // For sales, use selling price
      autoPrice = stockItem?.unitPrice?.toString() || "";
    } else if (selectedType === "Purchase" || selectedType === "Opening Balance") {
      // For purchases and opening balance, use last cost price
      autoPrice = stockItem?.lastCostPrice?.toString() || "";
    }
    
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              stockCode: code,
              stockDescription: stockItem?.description || "",
              unit: stockItem?.unitOfMeasure || "",
              unitPrice: autoPrice,
              errors: { ...item.errors, stockCode: undefined },
            }
          : item
      )
    );
  };

  const handleLineItemChange = (
    id: string,
    field: keyof LineItem,
    value: string
  ) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, [field]: value, errors: { ...item.errors, [field]: undefined } }
          : item
      )
    );
  };

  // Prepare warehouse options for combobox
  const warehouseOptions: ComboboxOption[] = mockWarehouses
    .filter(w => w.active)
    .map(w => ({
      value: w.code,
      label: w.name,
      description: w.code,
    }));

  // Get filtered locations for a specific warehouse
  const getLocationOptionsForWarehouse = (warehouseCode: string): ComboboxOption[] => {
    if (!warehouseCode) return [];
    
    return mockLocations
      .filter(l => l.active && l.warehouseCode === warehouseCode)
      .map(l => ({
        value: l.locationCode,
        label: l.locationCode,
        description: l.locationType,
      }));
  };

  // Prepare stock item options for combobox
  // Include both mock items and any prepopulated items from line items
  const stockItemOptions: ComboboxOption[] = (() => {
    const baseOptions = mockStockItems.map(item => ({
      value: item.code,
      label: `${item.code} - ${item.description}`,
      description: `${item.unitOfMeasure} | £${item.unitPrice.toFixed(2)}`,
    }));
    
    // Add prepopulated items that aren't in the mock data
    const existingCodes = new Set(mockStockItems.map(item => item.code));
    const prepopulatedOptions = lineItems
      .filter(item => item.stockCode && !existingCodes.has(item.stockCode))
      .map(item => ({
        value: item.stockCode,
        label: `${item.stockCode} - ${item.stockDescription}`,
        description: `${item.unit} | £${parseFloat(item.unitPrice || "0").toFixed(2)}`,
      }));
    
    // Remove duplicates from prepopulated options
    const uniquePrepopulated = prepopulatedOptions.filter(
      (option, index, self) => self.findIndex(o => o.value === option.value) === index
    );
    
    return [...baseOptions, ...uniquePrepopulated];
  })();

  // Handle warehouse change for line items
  const handleWarehouseChange = (id: string, warehouseCode: string) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              warehouse: warehouseCode,
              location: "", // Reset location when warehouse changes
              errors: { ...item.errors, warehouse: undefined, location: undefined },
            }
          : item
      )
    );
  };

  // Prepare project code options for combobox
  const projectCodeOptions: ComboboxOption[] = mockProjectCodes.map(project => ({
    value: project.code,
    label: `${project.code} - ${project.name}`,
    description: project.department,
  }));

  // Prepare cost center options for combobox
  const costCenterOptions: ComboboxOption[] = mockCostCenters.map(cc => ({
    value: cc.code,
    label: `${cc.code} - ${cc.name}`,
    description: cc.department,
  }));

  const addLineItem = () => {
    const newId = String(Date.now());
    setLineItems((prev) => [
      ...prev,
      {
        id: newId,
        stockCode: "",
        stockDescription: "",
        warehouse: "",
        location: "",
        movementDirection: "In" as LineMovementType,
        quantity: "",
        unitPrice: "",
        unit: "",
        projectCode: "",
        costCenter: "",
        notes: "",
        errors: {},
      },
    ]);
  };

  const removeLineItem = (id: string) => {
    if (lineItems.length === 1) {
      toast.error("At least one line item is required");
      return;
    }
    setLineItems((prev) => prev.filter((item) => item.id !== id));
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    let isValid = true;

    // Validate reference number
    if (!formData.referenceNumber.trim()) {
      errors.referenceNumber = "Reference number is required";
      isValid = false;
    }

    // Validate movement date
    if (!formData.movementDate) {
      errors.movementDate = "Movement date is required";
      isValid = false;
    } else if (formData.movementDate > new Date()) {
      errors.movementDate = "Movement date cannot be in the future";
      isValid = false;
    }

    // Type-specific validation
    if (selectedType === "Write-off") {
      if (!formData.writeOffReason) {
        errors.writeOffReason = "Write-off reason is required";
        isValid = false;
      }
      if (formData.writeOffReason === "other" && !formData.otherReasonDetails.trim()) {
        errors.otherReasonDetails = "Additional details are required when 'Other' is selected";
        isValid = false;
      }
    }

    if (selectedType === "Manual Adjustment") {
      if (!formData.adjustmentReason) {
        errors.adjustmentReason = "Adjustment reason is required";
        isValid = false;
      }
      if (formData.adjustmentReason === "other" && !formData.otherReasonDetails.trim()) {
        errors.otherReasonDetails = "Additional details are required when 'Other' is selected";
        isValid = false;
      }
    }

    if (selectedType === "Transfer Between Locations") {
      if (!formData.fromWarehouse) {
        errors.fromWarehouse = "From warehouse is required";
        isValid = false;
      }
      if (!formData.fromLocation) {
        errors.fromLocation = "From location is required";
        isValid = false;
      }
      if (!formData.toWarehouse) {
        errors.toWarehouse = "To warehouse is required";
        isValid = false;
      }
      if (!formData.toLocation) {
        errors.toLocation = "To location is required";
        isValid = false;
      }
      if (formData.fromWarehouse && formData.toWarehouse && formData.fromLocation && formData.toLocation) {
        const fromFullLocation = `${formData.fromWarehouse}-${formData.fromLocation}`;
        const toFullLocation = `${formData.toWarehouse}-${formData.toLocation}`;
        if (fromFullLocation === toFullLocation) {
          errors.toLocation = "To location must be different from From location";
          isValid = false;
        }
      }
    }

    setFormErrors(errors);

    // Validate line items
    const updatedLineItems = lineItems.map((item) => {
      const itemErrors: LineItem["errors"] = {};
      let itemValid = true;

      if (!item.stockCode) {
        itemErrors.stockCode = "Stock item is required";
        itemValid = false;
      }

      // Warehouse and Location are optional for all movement types except Transfer

      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        itemErrors.quantity = "Valid quantity is required";
        itemValid = false;
      }

      // Validate stock availability for transfers
      if (selectedType === "Transfer Between Locations" && item.stockCode && formData.fromWarehouse && formData.fromLocation) {
        const stockLevel = getStockLevel(item.stockCode, formData.fromWarehouse, formData.fromLocation);
        const requestedQty = parseFloat(item.quantity) || 0;
        
        if (stockLevel) {
          if (requestedQty > stockLevel.quantityAvailable) {
            itemErrors.quantity = `Insufficient stock. Only ${stockLevel.quantityAvailable} units available`;
            itemValid = false;
          }
        } else {
          itemErrors.quantity = "No stock available at source location";
          itemValid = false;
        }
      }

      // Validate price for Purchase, Sales, and Opening Balance
      if (selectedType === "Purchase" || selectedType === "Sales" || selectedType === "Opening Balance") {
        if (!item.unitPrice || parseFloat(item.unitPrice) <= 0) {
          itemErrors.unitPrice = selectedType === "Sales" ? "Valid price is required" : "Valid cost is required";
          itemValid = false;
        }
      }

      if (selectedType === "Manual Adjustment" && !item.adjustmentType) {
        itemErrors.adjustmentType = "Adjustment type is required";
        itemValid = false;
      }

      if (!itemValid) {
        isValid = false;
      }

      return { ...item, errors: itemErrors };
    });

    setLineItems(updatedLineItems);

    return isValid;
  };

  const handleSubmit = async () => {
    // Save functionality temporarily disabled
    return;
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelDialog(true);
    } else {
      navigateTo("stock-movements");
    }
  };

  const confirmCancel = () => {
    setShowCancelDialog(false);
    navigateTo("stock-movements");
  };

  // Debug: Log when selectedType changes
  useEffect(() => {
    console.log("=== Selected Type Changed ===");
    console.log("Selected Type:", selectedType);
    console.log("Is Transfer:", selectedType === "Transfer Between Locations");
  }, [selectedType]);

  const isTransferType = selectedType === "Transfer Between Locations";
  const showStandardForm = selectedType && !isTransferType;

  console.log("🎨 RENDER - selectedType:", selectedType);
  console.log("🎨 RENDER - isTransferType:", isTransferType);
  console.log("🎨 RENDER - showStandardForm:", showStandardForm);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">New Stock Movement</h1>
        <p className="text-muted-foreground">
          Create a new stock movement record
        </p>
      </div>

      {/* Pre-populated Items Banner */}
      {prepopulatedItemsCount > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                {prepopulatedItemsCount} item{prepopulatedItemsCount > 1 ? "s" : ""} pre-populated from Stock Items
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Items have been added with their current locations and costs. You can now select a movement type and enter quantities.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Movement Type Selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex-1 space-y-2">
                <Label htmlFor="movement-type" className="text-base">
                  Movement Type <span className="text-destructive">*</span>
                </Label>
                <Select value={selectedType || ""} onValueChange={handleTypeChange}>
                  <SelectTrigger 
                    id="movement-type" 
                    className="h-12 text-base"
                    ref={typeSelectRef}
                  >
                    <SelectValue placeholder="Select movement type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Sales">Sales</SelectItem>
                    <SelectItem value="Purchase">Purchase</SelectItem>
                    <SelectItem value="Opening Balance">Opening Balance</SelectItem>
                    <SelectItem value="Write-off">Write-off</SelectItem>
                    <SelectItem value="Manual Adjustment">Manual Adjustment</SelectItem>
                    <SelectItem value="Transfer Between Locations">Transfer Between Locations</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Choose the type of stock movement to record
                </p>
              </div>
              
              {selectedType && (
                <Badge className={cn("mt-8 px-3 py-1.5 text-sm", getTypeBadgeColor(selectedType))}>
                  {selectedType}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pre-Selection Helper Card */}
      {!selectedType && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <Info className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="space-y-3">
                <h3 className="font-semibold text-base">Select a movement type to begin</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <ShoppingCart className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-foreground">Sales:</strong> Record stock sold to customers
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <ShoppingBag className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-foreground">Purchase:</strong> Record stock received from suppliers
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <Database className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-foreground">Opening Balance:</strong> Set initial stock quantities
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <WriteOffIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-foreground">Write-off:</strong> Remove damaged or obsolete stock
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <Edit className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-foreground">Manual Adjustment:</strong> Correct stock quantities
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRightLeft className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-foreground">Transfer:</strong> Move stock between locations
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Standard Form (Sales, Purchase, Opening Balance, Write-off, Manual Adjustment) */}
      {showStandardForm && (
        <>
          {/* Header Section */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <h2 className="text-lg font-semibold">Movement Details</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Reference Number */}
                  <div className="space-y-2">
                    <Label htmlFor="reference">
                      {getReferenceLabel(selectedType)} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="reference"
                      value={formData.referenceNumber}
                      onChange={(e) =>
                        setFormData({ ...formData, referenceNumber: e.target.value })
                      }
                      placeholder="Enter reference number"
                    />
                    {formErrors.referenceNumber && (
                      <FieldError message={formErrors.referenceNumber} />
                    )}
                  </div>

                  {/* Movement Date */}
                  <div className="space-y-2">
                    <Label htmlFor="date">
                      Movement Date <span className="text-destructive">*</span>
                    </Label>
                    <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          id="date"
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !formData.movementDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.movementDate ? (
                            formatDate(formData.movementDate)
                          ) : (
                            <span>Pick a date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.movementDate}
                          onSelect={(date) => {
                            if (date) {
                              setFormData({ ...formData, movementDate: date });
                              setDatePickerOpen(false);
                            }
                          }}
                          disabled={(date) => date > new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {formErrors.movementDate && (
                      <FieldError message={formErrors.movementDate} />
                    )}
                  </div>

                  {/* Customer Reference (Sales only) */}
                  {selectedType === "Sales" && (
                    <div className="space-y-2">
                      <Label htmlFor="customer-ref">Customer Reference</Label>
                      <Input
                        id="customer-ref"
                        value={formData.customerReference}
                        onChange={(e) =>
                          setFormData({ ...formData, customerReference: e.target.value })
                        }
                        placeholder="Optional customer reference"
                      />
                    </div>
                  )}

                  {/* Supplier Reference (Purchase only) */}
                  {selectedType === "Purchase" && (
                    <div className="space-y-2">
                      <Label htmlFor="supplier-ref">Supplier Reference</Label>
                      <Input
                        id="supplier-ref"
                        value={formData.supplierReference}
                        onChange={(e) =>
                          setFormData({ ...formData, supplierReference: e.target.value })
                        }
                        placeholder="Optional supplier reference"
                      />
                    </div>
                  )}

                  {/* Write-off Reason (Write-off only) */}
                  {selectedType === "Write-off" && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="writeoff-reason">
                          Write-off Reason <span className="text-destructive">*</span>
                        </Label>
                        <Select
                          value={formData.writeOffReason}
                          onValueChange={(value) =>
                            setFormData({ ...formData, writeOffReason: value })
                          }
                        >
                          <SelectTrigger id="writeoff-reason">
                            <SelectValue placeholder="Select reason..." />
                          </SelectTrigger>
                          <SelectContent>
                            {writeOffReasons.map((reason) => (
                              <SelectItem key={reason.value} value={reason.value}>
                                {reason.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {formErrors.writeOffReason && (
                          <FieldError message={formErrors.writeOffReason} />
                        )}
                      </div>

                      {formData.writeOffReason === "other" && (
                        <div className="space-y-2">
                          <Label htmlFor="other-details">
                            Additional Details <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="other-details"
                            value={formData.otherReasonDetails}
                            onChange={(e) =>
                              setFormData({ ...formData, otherReasonDetails: e.target.value })
                            }
                            placeholder="Please specify..."
                          />
                          {formErrors.otherReasonDetails && (
                            <FieldError message={formErrors.otherReasonDetails} />
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Adjustment Reason (Manual Adjustment only) */}
                  {selectedType === "Manual Adjustment" && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="adjustment-reason">
                          Adjustment Reason <span className="text-destructive">*</span>
                        </Label>
                        <Select
                          value={formData.adjustmentReason}
                          onValueChange={(value) =>
                            setFormData({ ...formData, adjustmentReason: value })
                          }
                        >
                          <SelectTrigger id="adjustment-reason">
                            <SelectValue placeholder="Select reason..." />
                          </SelectTrigger>
                          <SelectContent>
                            {adjustmentReasons.map((reason) => (
                              <SelectItem key={reason.value} value={reason.value}>
                                {reason.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {formErrors.adjustmentReason && (
                          <FieldError message={formErrors.adjustmentReason} />
                        )}
                      </div>

                      {formData.adjustmentReason === "other" && (
                        <div className="space-y-2">
                          <Label htmlFor="other-details">
                            Additional Details <span className="text-destructive">*</span>
                          </Label>
                          <Input
                            id="other-details"
                            value={formData.otherReasonDetails}
                            onChange={(e) =>
                              setFormData({ ...formData, otherReasonDetails: e.target.value })
                            }
                            placeholder="Please specify..."
                          />
                          {formErrors.otherReasonDetails && (
                            <FieldError message={formErrors.otherReasonDetails} />
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Optional notes..."
                    rows={3}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Line Items Section */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Line Items</h2>
                  <Button onClick={addLineItem} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>

                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Stock Item *</TableHead>
                        {selectedType !== "Transfer Between Locations" && (
                          <>
                            <TableHead className="w-[150px]">Warehouse</TableHead>
                            <TableHead className="w-[150px]">Location</TableHead>
                          </>
                        )}
                        <TableHead className="w-[100px]">Movement Type</TableHead>
                        <TableHead className="w-[100px]">Quantity *</TableHead>
                        <TableHead className="w-[100px]">Unit Price</TableHead>
                        <TableHead className="w-[100px]">Line Total</TableHead>
                        <TableHead className="w-[80px]">Unit</TableHead>
                        <TableHead className="w-[150px]">Project Code</TableHead>
                        <TableHead className="w-[150px]">Cost Center</TableHead>
                        <TableHead className="w-[60px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lineItems.map((item, index) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div className="space-y-1">
                              <Combobox
                                options={stockItemOptions}
                                value={item.stockCode}
                                onValueChange={(value) => handleStockCodeChange(item.id, value)}
                                placeholder="Search stock..."
                                searchPlaceholder="Search by code or name..."
                                emptyText={
                                  selectedType === "Transfer Between Locations" && 
                                  formData.fromWarehouse && 
                                  formData.fromLocation && 
                                  stockItemOptions.length === 0
                                    ? "No items with available stock at source location."
                                    : "No stock items found."
                                }
                                className={cn(item.errors.stockCode && "border-destructive")}
                              />
                              {item.errors.stockCode && (
                                <p className="text-xs text-destructive">{item.errors.stockCode}</p>
                              )}
                            </div>
                          </TableCell>

                          {selectedType !== "Transfer Between Locations" && (
                            <>
                              <TableCell>
                                <Combobox
                                  options={warehouseOptions}
                                  value={item.warehouse}
                                  onValueChange={(value) => handleWarehouseChange(item.id, value)}
                                  placeholder="Select..."
                                  searchPlaceholder="Search warehouses..."
                                  emptyText="No warehouses found."
                                />
                              </TableCell>

                              <TableCell>
                                <Combobox
                                  options={getLocationOptionsForWarehouse(item.warehouse)}
                                  value={item.location}
                                  onValueChange={(value) =>
                                    handleLineItemChange(item.id, "location", value)
                                  }
                                  placeholder="Select..."
                                  searchPlaceholder="Search locations..."
                                  emptyText={item.warehouse ? "No locations found." : "Select warehouse first"}
                                  disabled={!item.warehouse}
                                />
                              </TableCell>
                            </>
                          )}

                          <TableCell>
                            <Select
                              value={item.movementDirection}
                              onValueChange={(value) =>
                                handleLineItemChange(item.id, "movementDirection", value)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="In">In</SelectItem>
                                <SelectItem value="Out">Out</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>

                          <TableCell>
                            <div className="space-y-1">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) =>
                                  handleLineItemChange(item.id, "quantity", e.target.value)
                                }
                                placeholder="0"
                                className={cn(item.errors.quantity && "border-destructive")}
                              />
                              {item.errors.quantity && (
                                <p className="text-xs text-destructive">{item.errors.quantity}</p>
                              )}
                            </div>
                          </TableCell>

                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unitPrice}
                              readOnly
                              disabled
                              placeholder="0.00"
                              className="bg-muted cursor-not-allowed"
                              title="Unit price is read-only (from stock item)"
                            />
                          </TableCell>

                          <TableCell>
                            <span className="text-sm font-medium">
                              £{((parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0)).toFixed(2)}
                            </span>
                          </TableCell>

                          <TableCell>
                            <span className="text-sm text-muted-foreground">{item.unit || "-"}</span>
                          </TableCell>

                          <TableCell>
                            <Combobox
                              options={projectCodeOptions}
                              value={item.projectCode}
                              onValueChange={(value) =>
                                handleLineItemChange(item.id, "projectCode", value)
                              }
                              placeholder="Select..."
                              searchPlaceholder="Search projects..."
                              emptyText="No projects found."
                            />
                          </TableCell>

                          <TableCell>
                            <Combobox
                              options={costCenterOptions}
                              value={item.costCenter}
                              onValueChange={(value) =>
                                handleLineItemChange(item.id, "costCenter", value)
                              }
                              placeholder="Select..."
                              searchPlaceholder="Search cost centers..."
                              emptyText="No cost centers found."
                            />
                          </TableCell>

                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeLineItem(item.id)}
                              disabled={lineItems.length === 1}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {/* Summary Row */}
                      {lineItems.length > 0 && (
                        <TableRow className="bg-muted/50 font-medium border-t-2">
                          <TableCell colSpan={selectedType !== "Transfer Between Locations" ? 4 : 2} className="text-right">
                            Total:
                          </TableCell>
                          <TableCell>
                            {lineItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0).toFixed(2)} units
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell>
                            £{lineItems.reduce((sum, item) => 
                              sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0)), 0
                            ).toFixed(2)}
                          </TableCell>
                          <TableCell colSpan={4}></TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cost Impact Warning */}
          {lineItems.length > 0 && lineItems.some(item => item.stockCode) && (
            <CostImpactWarning
              movementType={selectedType || ""}
              items={lineItems
                .filter(item => item.stockCode)
                .map(item => {
                  const stockItem = mockStockItems.find(s => s.code === item.stockCode);
                  return {
                    stockCode: item.stockCode,
                    description: stockItem?.description || "",
                    quantity: parseFloat(item.quantity) || 0,
                    unitPrice: parseFloat(item.unitPrice || "0"),
                    currentAvgCost: stockItem?.averageCost || 0,
                    currentQty: stockItem?.physicalQuantity || 0,
                  };
                })}
            />
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button onClick={handleSubmit} disabled={isSaving} className="flex-1 sm:flex-none">
              {isSaving ? (
                <>
                  <Package className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Package className="mr-2 h-4 w-4" />
                  Submit Movement
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
          </div>
        </>
      )}

      {/* Transfer Form */}
      {isTransferType && (
        <>
          {/* Header Section */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <h2 className="text-lg font-semibold">Transfer Details</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Reference Number */}
                  <div className="space-y-2">
                    <Label htmlFor="reference">
                      Transfer Reference <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="reference"
                      value={formData.referenceNumber}
                      onChange={(e) =>
                        setFormData({ ...formData, referenceNumber: e.target.value })
                      }
                      placeholder="Enter reference number"
                    />
                    {formErrors.referenceNumber && (
                      <FieldError message={formErrors.referenceNumber} />
                    )}
                  </div>

                  {/* Movement Date */}
                  <div className="space-y-2">
                    <Label htmlFor="date">
                      Movement Date <span className="text-destructive">*</span>
                    </Label>
                    <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          id="date"
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !formData.movementDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.movementDate ? (
                            formatDate(formData.movementDate)
                          ) : (
                            <span>Pick a date</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.movementDate}
                          onSelect={(date) => {
                            if (date) {
                              setFormData({ ...formData, movementDate: date });
                              setDatePickerOpen(false);
                            }
                          }}
                          disabled={(date) => date > new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {formErrors.movementDate && (
                      <FieldError message={formErrors.movementDate} />
                    )}
                  </div>

                  {/* From Warehouse */}
                  <div className="space-y-2">
                    <Label htmlFor="from-warehouse">
                      From Warehouse <span className="text-destructive">*</span>
                    </Label>
                    <Combobox
                      options={warehouseOptions}
                      value={formData.fromWarehouse}
                      onValueChange={(value) =>
                        setFormData({ ...formData, fromWarehouse: value, fromLocation: "" })
                      }
                      placeholder="Select warehouse..."
                      searchPlaceholder="Search warehouses..."
                      emptyText="No warehouses found."
                    />
                    {formErrors.fromWarehouse && (
                      <FieldError message={formErrors.fromWarehouse} />
                    )}
                  </div>

                  {/* From Location */}
                  <div className="space-y-2">
                    <Label htmlFor="from-location">
                      From Location <span className="text-destructive">*</span>
                    </Label>
                    <Combobox
                      options={getLocationOptionsForWarehouse(formData.fromWarehouse)}
                      value={formData.fromLocation}
                      onValueChange={(value) =>
                        setFormData({ ...formData, fromLocation: value })
                      }
                      placeholder="Select location..."
                      searchPlaceholder="Search locations..."
                      emptyText={formData.fromWarehouse ? "No locations found." : "Select warehouse first"}
                      disabled={!formData.fromWarehouse}
                    />
                    {formErrors.fromLocation && (
                      <FieldError message={formErrors.fromLocation} />
                    )}
                  </div>

                  {/* To Warehouse */}
                  <div className="space-y-2">
                    <Label htmlFor="to-warehouse">
                      To Warehouse <span className="text-destructive">*</span>
                    </Label>
                    <Combobox
                      options={warehouseOptions}
                      value={formData.toWarehouse}
                      onValueChange={(value) =>
                        setFormData({ ...formData, toWarehouse: value, toLocation: "" })
                      }
                      placeholder="Select warehouse..."
                      searchPlaceholder="Search warehouses..."
                      emptyText="No warehouses found."
                    />
                    {formErrors.toWarehouse && (
                      <FieldError message={formErrors.toWarehouse} />
                    )}
                  </div>

                  {/* To Location */}
                  <div className="space-y-2">
                    <Label htmlFor="to-location">
                      To Location <span className="text-destructive">*</span>
                    </Label>
                    <Combobox
                      options={getLocationOptionsForWarehouse(formData.toWarehouse)}
                      value={formData.toLocation}
                      onValueChange={(value) =>
                        setFormData({ ...formData, toLocation: value })
                      }
                      placeholder="Select location..."
                      searchPlaceholder="Search locations..."
                      emptyText={formData.toWarehouse ? "No locations found." : "Select warehouse first"}
                      disabled={!formData.toWarehouse}
                    />
                    {formErrors.toLocation && (
                      <FieldError message={formErrors.toLocation} />
                    )}
                  </div>
                </div>

                {/* Stock Information Card - Shows when item is selected in line items */}
                {lineItems.length > 0 && lineItems[0].stockCode && formData.fromWarehouse && formData.fromLocation && (
                  <div className="mt-6">
                    <StockInfoCard
                      stockCode={lineItems[0].stockCode}
                      warehouseCode={formData.fromWarehouse}
                      locationCode={formData.fromLocation}
                      requestedQuantity={lineItems[0].quantity || undefined}
                    />
                  </div>
                )}

                {/* Transfer Reason */}
                <div className="space-y-2">
                  <Label htmlFor="transfer-reason">Reason for Transfer</Label>
                  <Select
                    value={formData.notes}
                    onValueChange={(value) => setFormData({ ...formData, notes: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select reason..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="replenishment">Stock Replenishment</SelectItem>
                      <SelectItem value="rebalancing">Warehouse Rebalancing</SelectItem>
                      <SelectItem value="customer_request">Customer Request</SelectItem>
                      <SelectItem value="quality_issue">Quality Issue</SelectItem>
                      <SelectItem value="consolidation">Stock Consolidation</SelectItem>
                      <SelectItem value="relocation">Location Relocation</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Additional Notes */}
                <div className="space-y-2">
                  <Label htmlFor="additional-notes">Additional Notes</Label>
                  <Textarea
                    id="additional-notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Optional additional details..."
                    rows={2}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Line Items Section */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Items to Transfer</h2>
                  <Button onClick={addLineItem} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>

                {/* Info message about filtered items */}
                {formData.fromWarehouse && formData.fromLocation && (
                  <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-900">
                      Only items with available stock at <strong>{formData.fromWarehouse} - {formData.fromLocation}</strong> are shown in the dropdown.
                    </p>
                  </div>
                )}

                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[250px]">Stock Item *</TableHead>
                        <TableHead className="w-[150px]">Quantity *</TableHead>
                        <TableHead className="w-[150px]">Available at Source</TableHead>
                        <TableHead className="w-[100px]">Unit</TableHead>
                        <TableHead className="flex-1">Notes</TableHead>
                        <TableHead className="w-[80px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lineItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div className="space-y-1">
                              <Combobox
                                options={stockItemOptions}
                                value={item.stockCode}
                                onValueChange={(value) => handleStockCodeChange(item.id, value)}
                                placeholder="Search stock..."
                                searchPlaceholder="Search by code or name..."
                                emptyText={
                                  selectedType === "Transfer Between Locations" && 
                                  formData.fromWarehouse && 
                                  formData.fromLocation && 
                                  stockItemOptions.length === 0
                                    ? "No items with available stock at source location."
                                    : "No stock items found."
                                }
                                className={cn(item.errors.stockCode && "border-destructive")}
                              />
                              {item.errors.stockCode && (
                                <p className="text-xs text-destructive">{item.errors.stockCode}</p>
                              )}
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className="space-y-1">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) =>
                                  handleLineItemChange(item.id, "quantity", e.target.value)
                                }
                                placeholder="0"
                                className={cn(item.errors.quantity && "border-destructive")}
                              />
                              {item.errors.quantity && (
                                <p className="text-xs text-destructive">{item.errors.quantity}</p>
                              )}
                            </div>
                          </TableCell>

                          <TableCell>
                            {item.stockCode && formData.fromWarehouse && formData.fromLocation ? (
                              <StockInfoInline
                                stockCode={item.stockCode}
                                warehouseCode={formData.fromWarehouse}
                                locationCode={formData.fromLocation}
                                requestedQuantity={parseFloat(item.quantity) || 0}
                              />
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                {!item.stockCode ? "Select item" : "Select location"}
                              </span>
                            )}
                          </TableCell>

                          <TableCell>
                            <span className="text-sm text-muted-foreground">{item.unit || "-"}</span>
                          </TableCell>

                          <TableCell>
                            <Input
                              value={item.notes}
                              onChange={(e) =>
                                handleLineItemChange(item.id, "notes", e.target.value)
                              }
                              placeholder="Optional..."
                            />
                          </TableCell>

                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeLineItem(item.id)}
                              disabled={lineItems.length === 1}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {/* Summary Row */}
                      {(selectedType === "Purchase" || selectedType === "Sales" || selectedType === "Opening Balance") && lineItems.length > 0 && (
                        <TableRow className="bg-muted/50 font-medium border-t-2">
                          <TableCell colSpan={selectedType !== "Transfer Between Locations" ? 4 : 2} className="text-right">
                            Total:
                          </TableCell>
                          <TableCell>
                            {lineItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0).toFixed(2)} units
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell>
                            £{lineItems.reduce((sum, item) => 
                              sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0)), 0
                            ).toFixed(2)}
                          </TableCell>
                          <TableCell colSpan={3}></TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cost Impact Warning */}
          {lineItems.length > 0 && lineItems.some(item => item.stockCode) && (
            <CostImpactWarning
              movementType={selectedType || ""}
              items={lineItems
                .filter(item => item.stockCode)
                .map(item => {
                  const stockItem = mockStockItems.find(s => s.code === item.stockCode);
                  return {
                    stockCode: item.stockCode,
                    description: stockItem?.description || "",
                    quantity: parseFloat(item.quantity) || 0,
                    unitPrice: parseFloat(item.unitPrice || "0"),
                    currentAvgCost: stockItem?.averageCost || 0,
                    currentQty: stockItem?.physicalQuantity || 0,
                  };
                })}
            />
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button onClick={handleSubmit} disabled={isSaving} className="flex-1 sm:flex-none">
              {isSaving ? (
                <>
                  <ArrowRightLeft className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ArrowRightLeft className="mr-2 h-4 w-4" />
                  Complete Transfer
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
          </div>
        </>
      )}

      {/* Type Change Confirmation Dialog */}
      <AlertDialog open={showTypeChangeDialog} onOpenChange={setShowTypeChangeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Movement Type?</AlertDialogTitle>
            <AlertDialogDescription>
              Changing the movement type will clear all entered data. Are you sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelTypeChange}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmTypeChange} className="bg-destructive hover:bg-destructive/90">
              Change Type
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? All data will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} className="bg-destructive hover:bg-destructive/90">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
